'use client'

import { useKeyPress } from '@/hooks'
import { cn } from '@/lib/utils'
import { Icon } from '@/ui/components'
import {
  HTMLAttributes,
  ReactNode,
  Ref,
  forwardRef,
  memo,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import { useOnClickOutside } from 'usehooks-ts'

interface Props extends HTMLAttributes<HTMLDivElement> {
  body: ReactNode
  head: ReactNode
  label?: string
  closeOnClickedOutside?: boolean
  closeOnEscape?: boolean
  icon?: string
  isAutoHeight?: boolean
}

const DropDown = forwardRef(
  (
    {
      body,
      head,
      isAutoHeight,
      label,
      closeOnClickedOutside,
      closeOnEscape,
      onMouseEnter,
      icon,
      onMouseLeave,
      onClick,
      className,
      ...props
    }: Props,
    ref: Ref<HTMLDivElement>
  ) => {
    const comp = useRef<any>(null)
    const headRef = useRef<HTMLDivElement>(null)
    const bodyRef = useRef<HTMLDivElement>(null)
    //states
    const [isOpen, setIsOpen] = useState(false)

    // Accessibility: stable ids
    const reactId = useId()
    const headerId = `dropdown-header-${reactId}`
    const bodyId = `dropdown-body-${reactId}`

    //stores

    //api
    useImperativeHandle(ref, () => comp.current as HTMLDivElement)

    //hooks
    useOnClickOutside(comp, () => {
      if (closeOnClickedOutside) {
        setIsOpen(false)
        headRef.current?.focus()
      }
    })
    useKeyPress('Escape', () => {
      closeOnEscape && setIsOpen(false)
    })

    const handleClick = (e: any) => {
      setIsOpen(!isOpen)
      onClick && onClick(e)
    }

    const getFocusableElements = (container: HTMLElement | null) => {
      if (!container) {
        return [] as HTMLElement[]
      }
      const selectors = [
        'button:not([disabled])',
        'a[href]',
        'input:not([disabled])',
        'textarea:not([disabled])',
        'select:not([disabled])',
        '[role="option"]',
        '[role="menuitem"]',
        '[tabindex]:not([tabindex="-1"])'
      ]
      return Array.from(
        container.querySelectorAll(selectors.join(', '))
      ) as HTMLElement[]
    }

    const focusFirstItem = () => {
      const items = getFocusableElements(bodyRef.current)
      items[0]?.focus()
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        const willOpen = !isOpen
        setIsOpen(willOpen)
        if (willOpen) {
          // focus first item after open
          setTimeout(focusFirstItem, 0)
        }
      } else if (event.key === 'ArrowDown') {
        event.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
          setTimeout(focusFirstItem, 0)
        } else {
          focusFirstItem()
        }
      } else if (event.key === 'ArrowUp' && isOpen) {
        event.preventDefault()
        const items = getFocusableElements(bodyRef.current)
        if (items.length > 0) {
          items[items.length - 1].focus()
        }
      }
    }

    // Handle keyboard navigation inside dropdown body
    const handleBodyKeyDown = (event: React.KeyboardEvent) => {
      const items = getFocusableElements(bodyRef.current)
      const currentIndex = items.findIndex(
        (item) => item === document.activeElement
      )

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          if (currentIndex < items.length - 1) {
            items[currentIndex + 1].focus()
          } else {
            items[0]?.focus() // wrap to first
          }
          break
        case 'ArrowUp':
          event.preventDefault()
          if (currentIndex > 0) {
            items[currentIndex - 1].focus()
          } else {
            items[items.length - 1]?.focus() // wrap to last
          }
          break
        case 'Home':
          event.preventDefault()
          items[0]?.focus()
          break
        case 'End':
          event.preventDefault()
          items[items.length - 1]?.focus()
          break
        case 'Escape':
          event.preventDefault()
          setIsOpen(false)
          headRef.current?.focus()
          break
        case 'Tab':
          // Trap focus within dropdown when open
          if (isOpen) {
            if (event.shiftKey && currentIndex === 0) {
              event.preventDefault()
              items[items.length - 1]?.focus()
            } else if (!event.shiftKey && currentIndex === items.length - 1) {
              event.preventDefault()
              items[0]?.focus()
            }
          }
          break
      }
    }

    useEffect(() => {
      if (!isOpen) {
        // return focus to trigger if closing via code
        headRef.current &&
          headRef.current !== document.activeElement &&
          headRef.current.focus()
      }
    }, [isOpen])

    const handleHover = (e: any) => {
      onMouseEnter && onMouseEnter(e)
    }

    const handleHoverOut = (e: any) => {
      onMouseLeave && onMouseLeave(e)
    }

    const dropdwonClasses = cn(
      'global-focus relative h-full w-full cursor-pointer justify-self-start bg-neutral-200',
      isOpen &&
        '[&_.body]:visible [&_.body]:opacity-100 [&_[data-icon]]:rotate-180',
      className
    )

    return (
      <div
        {...props}
        className={dropdwonClasses}
        ref={comp}
        onMouseEnter={handleHover}
        onMouseLeave={handleHoverOut}
      >
        <div
          className="p-md py-xs relative z-1 flex h-full items-center justify-between"
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          data-head
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          aria-controls={bodyId}
          id={headerId}
          aria-haspopup="listbox"
          ref={headRef}
        >
          <div>
            {label && (
              <div className="text-xs leading-[1em] font-semibold">{label}</div>
            )}
            <span className="textstyle-md-regular text-neutral-400">
              {head}
            </span>
          </div>

          <Icon
            icon={icon || 'material-symbols:expand-more'}
            className="relative right-0 size-6 origin-center transition-transform"
            data-icon
          />
        </div>
        <div
          className={cn(
            'body gap-y-gutter p-md px-md invisible absolute top-0 z-20 flex max-h-60 w-full translate-y-full flex-col justify-items-start overflow-scroll bg-neutral-200 text-sm opacity-0 shadow-xl transition-[visibility,opacity] duration-[0s,var(--duration-default)] ease-[linear,var(--ease-out-cubic)] motion-reduce:visible motion-reduce:opacity-100',
            isAutoHeight && 'max-h-none'
          )}
          data-lenis-prevent
          data-body
          id={bodyId}
          role="listbox"
          aria-labelledby={headerId}
          aria-hidden={!isOpen}
          tabIndex={-1}
          ref={bodyRef}
          onKeyDown={handleBodyKeyDown}
        >
          {body}
        </div>
      </div>
    )
  }
)

DropDown.displayName = 'DropDown'

export default memo(DropDown)
